import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Award, TrendingUp, TrendingDown, Minus, Target, AlertTriangle,
  CheckCircle2, XCircle, Clock, RefreshCw, Download, Info,
  Activity, Users, FileCheck, DollarSign, Heart, Sparkles,
  ChevronRight, ExternalLink, Zap
} from 'lucide-react';
import PDFExportButton from '@/components/common/PDFExportButton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons and colors
const categoryConfig = {
  Quality: { icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-200' },
  Operations: { icon: Activity, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' },
  Workforce: { icon: Users, color: 'text-violet-500', bg: 'bg-violet-50', border: 'border-violet-200' },
  Compliance: { icon: FileCheck, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  Financial: { icon: DollarSign, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200' }
};

// Status configurations
const statusConfig = {
  achieved: { color: 'bg-emerald-500', text: 'text-emerald-700', label: 'Achieved', icon: CheckCircle2 },
  on_track: { color: 'bg-blue-500', text: 'text-blue-700', label: 'On Track', icon: TrendingUp },
  at_risk: { color: 'bg-amber-500', text: 'text-amber-700', label: 'At Risk', icon: AlertTriangle },
  behind: { color: 'bg-orange-500', text: 'text-orange-700', label: 'Behind', icon: Clock },
  critical: { color: 'bg-red-500', text: 'text-red-700', label: 'Critical', icon: XCircle },
  no_data: { color: 'bg-gray-400', text: 'text-gray-600', label: 'No Data', icon: Minus }
};

// Excellence level badges
const excellenceLevelConfig = {
  'World-Class': { color: 'bg-gradient-to-r from-yellow-400 to-amber-500', text: 'text-white', icon: '🏆' },
  'Excellent': { color: 'bg-gradient-to-r from-emerald-400 to-green-500', text: 'text-white', icon: '⭐' },
  'High Performing': { color: 'bg-gradient-to-r from-blue-400 to-indigo-500', text: 'text-white', icon: '🎯' },
  'Good': { color: 'bg-gradient-to-r from-cyan-400 to-blue-500', text: 'text-white', icon: '✓' },
  'Developing': { color: 'bg-gradient-to-r from-amber-400 to-orange-500', text: 'text-white', icon: '📈' },
  'Needs Improvement': { color: 'bg-gradient-to-r from-red-400 to-rose-500', text: 'text-white', icon: '⚠️' }
};

export default function HospiceExcellence() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [actions, setActions] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  const [forecasts, setForecasts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('qapi-token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [goalsRes, trendsRes, actionsRes, benchmarksRes, forecastsRes] = await Promise.all([
        fetch(`${API_URL}/api/hospice-excellence/goals`, { headers }),
        fetch(`${API_URL}/api/hospice-excellence/trends?weeks=12`, { headers }),
        fetch(`${API_URL}/api/hospice-excellence/actions`, { headers }),
        fetch(`${API_URL}/api/hospice-excellence/benchmarks`, { headers }),
        fetch(`${API_URL}/api/hospice-excellence/forecasts`, { headers })
      ]);

      if (goalsRes.ok) {
        const goalsData = await goalsRes.json();
        setData(goalsData);
      }
      
      if (trendsRes.ok) {
        const trendsData = await trendsRes.json();
        setTrends(trendsData);
      }
      
      if (actionsRes.ok) {
        const actionsData = await actionsRes.json();
        setActions(actionsData.actions || []);
      }
      
      if (benchmarksRes.ok) {
        const benchmarksData = await benchmarksRes.json();
        setBenchmarks(benchmarksData);
      }
      
      if (forecastsRes.ok) {
        const forecastsData = await forecastsRes.json();
        setForecasts(forecastsData);
      }
    } catch (error) {
      console.error('Failed to fetch excellence data:', error);
      toast.error('Failed to load excellence data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRecordSnapshot = async () => {
    setRefreshing(true);
    const token = localStorage.getItem('qapi-token');
    
    try {
      const res = await fetch(`${API_URL}/api/hospice-excellence/snapshot`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Weekly snapshot recorded successfully');
        fetchData();
      } else {
        toast.error('Failed to record snapshot');
      }
    } catch (error) {
      console.error('Failed to record snapshot:', error);
      toast.error('Failed to record snapshot');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <Award className="h-12 w-12 text-violet-500 animate-pulse mx-auto" />
          <p className="text-muted-foreground">Loading excellence metrics...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <p className="text-muted-foreground">Unable to load excellence data</p>
        <Button onClick={fetchData} className="mt-4">Try Again</Button>
      </div>
    );
  }

  const levelConfig = excellenceLevelConfig[data.excellence_level] || excellenceLevelConfig['Developing'];

  return (
    <div className="space-y-6" data-testid="hospice-excellence-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Award className="h-8 w-8 text-violet-500" />
            <h1 className="text-2xl lg:text-3xl font-bold">Hospice Excellence</h1>
            <Badge className={`${levelConfig.color} ${levelConfig.text} px-3 py-1`}>
              {levelConfig.icon} {data.excellence_level}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            CMS/CHAP 2025 Quality Standards & Performance Tracking
          </p>
        </div>
        
        <div className="flex gap-2">
          <PDFExportButton 
            title="Hospice Excellence Report"
            filename="hospice-excellence-report"
            contentId="hospice-excellence-page"
          />
          <Button variant="outline" onClick={fetchData} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleRecordSnapshot} disabled={refreshing}>
            <Sparkles className="h-4 w-4 mr-2" />
            Record Weekly Snapshot
          </Button>
        </div>
      </div>

      {/* Overall Score Card */}
      <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="col-span-1 md:col-span-2">
              <p className="text-violet-200 text-sm font-medium">Overall Excellence Score</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-5xl font-bold">{data.overall_excellence_score}</span>
                <span className="text-2xl text-violet-200">/100</span>
              </div>
              <Progress 
                value={data.overall_excellence_score} 
                className="mt-3 h-3 bg-violet-400/30"
              />
              {trends?.improvement !== undefined && (
                <div className="flex items-center gap-2 mt-3">
                  {trends.improvement > 0 ? (
                    <TrendingUp className="h-4 w-4 text-emerald-300" />
                  ) : trends.improvement < 0 ? (
                    <TrendingDown className="h-4 w-4 text-rose-300" />
                  ) : (
                    <Minus className="h-4 w-4 text-violet-300" />
                  )}
                  <span className="text-sm text-violet-200">
                    {trends.improvement > 0 ? '+' : ''}{trends.improvement} points over {trends.weeks?.length || 0} weeks
                  </span>
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-violet-200 text-xs">Goals Met</p>
                <p className="text-2xl font-bold">{data.goals_met} / {data.total_goals}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-violet-200 text-xs">Goals at Risk</p>
                <p className="text-2xl font-bold text-amber-300">{data.goals_at_risk}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <p className="text-violet-200 text-xs font-medium mb-2">Category Scores</p>
              {Object.entries(data.category_scores || {}).map(([cat, score]) => (
                <div key={cat} className="flex items-center justify-between">
                  <span className="text-sm">{cat}</span>
                  <div className="flex items-center gap-2">
                    <Progress value={score} className="w-16 h-2 bg-violet-400/30" />
                    <span className="text-xs w-8">{score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="goals">All Goals</TabsTrigger>
          <TabsTrigger value="forecasts">Forecasts</TabsTrigger>
          <TabsTrigger value="actions">Priority Actions</TabsTrigger>
          <TabsTrigger value="benchmarks">Benchmarks</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Category Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(data.goals_by_category || {}).map(([category, goals]) => {
              const config = categoryConfig[category] || categoryConfig.Quality;
              const CategoryIcon = config.icon;
              const catScore = data.category_scores?.[category] || 0;
              const criticalCount = goals.filter(g => g.status === 'critical' || g.status === 'at_risk').length;
              
              return (
                <Card 
                  key={category} 
                  className={`${config.bg} ${config.border} border-2 hover:shadow-lg transition-shadow cursor-pointer`}
                  onClick={() => setActiveTab('goals')}
                  data-testid={`category-card-${category.toLowerCase()}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CategoryIcon className={`h-5 w-5 ${config.color}`} />
                        <CardTitle className="text-lg">{category}</CardTitle>
                      </div>
                      <Badge variant="outline" className={criticalCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                        {criticalCount > 0 ? `${criticalCount} at risk` : 'All good'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl font-bold">{catScore}%</span>
                      <span className="text-sm text-muted-foreground">{goals.length} goals</span>
                    </div>
                    <Progress value={catScore} className="h-2" />
                    
                    {/* Top 2 goals preview */}
                    <div className="mt-4 space-y-2">
                      {goals.slice(0, 2).map(goal => {
                        const statusCfg = statusConfig[goal.status] || statusConfig.no_data;
                        const StatusIcon = statusCfg.icon;
                        return (
                          <div key={goal.id} className="flex items-center justify-between text-sm">
                            <span className="truncate flex-1">{goal.name}</span>
                            <StatusIcon className={`h-4 w-4 ${statusCfg.text} shrink-0 ml-2`} />
                          </div>
                        );
                      })}
                      {goals.length > 2 && (
                        <p className="text-xs text-muted-foreground">+{goals.length - 2} more goals</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Priority Actions Preview */}
          {actions.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    <CardTitle>Priority Actions</CardTitle>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('actions')}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
                <CardDescription>Top actions to improve your excellence score</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {actions.slice(0, 3).map((action, idx) => (
                    <div 
                      key={action.goal_id} 
                      className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <Badge 
                        variant={action.priority === 'CRITICAL' ? 'destructive' : action.priority === 'HIGH' ? 'default' : 'secondary'}
                        className="shrink-0"
                      >
                        {action.priority}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{action.goal_name}</p>
                        <p className="text-sm text-muted-foreground truncate">{action.recommendation}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium">{action.current_value ?? 'N/A'} → {action.target}</p>
                        <p className="text-xs text-muted-foreground">{action.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* All Goals Tab */}
        <TabsContent value="goals" className="space-y-4">
          {Object.entries(data.goals_by_category || {}).map(([category, goals]) => {
            const config = categoryConfig[category] || categoryConfig.Quality;
            const CategoryIcon = config.icon;
            
            return (
              <Card key={category}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <CategoryIcon className={`h-5 w-5 ${config.color}`} />
                    <CardTitle>{category}</CardTitle>
                    <Badge variant="outline">{data.category_scores?.[category] || 0}%</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {goals.map(goal => {
                      const statusCfg = statusConfig[goal.status] || statusConfig.no_data;
                      const StatusIcon = statusCfg.icon;
                      
                      return (
                        <div 
                          key={goal.id} 
                          className="p-4 rounded-lg border hover:shadow-sm transition-shadow"
                          data-testid={`goal-${goal.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium">{goal.name}</h4>
                                <Badge variant="outline" className={`${statusCfg.text} text-xs`}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {statusCfg.label}
                                </Badge>
                                {goal.priority === 'critical' && (
                                  <Badge variant="destructive" className="text-xs">Critical</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{goal.description}</p>
                            </div>
                            
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Info className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-xs">
                                  <p><strong>Source:</strong> {goal.benchmark_source}</p>
                                  <p><strong>Percentile:</strong> {goal.benchmark_percentile}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          
                          <div className="mt-3 grid grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">Current</p>
                              <p className="text-lg font-semibold">
                                {goal.current_value !== null ? `${goal.current_value}${goal.unit}` : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Target</p>
                              <p className="text-lg font-semibold">{goal.target}{goal.unit}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Gap</p>
                              <p className={`text-lg font-semibold ${goal.gap > 0 ? 'text-amber-600' : goal.gap < 0 ? 'text-emerald-600' : ''}`}>
                                {goal.gap !== null ? `${goal.gap > 0 ? '+' : ''}${goal.gap}${goal.unit}` : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Trend</p>
                              <div className="flex items-center gap-1">
                                {goal.trend === 'improving' && <TrendingUp className="h-4 w-4 text-emerald-500" />}
                                {goal.trend === 'declining' && <TrendingDown className="h-4 w-4 text-red-500" />}
                                {goal.trend === 'stable' && <Minus className="h-4 w-4 text-gray-500" />}
                                <span className="text-sm capitalize">{goal.trend}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                              <span>Progress</span>
                              <span>{goal.progress}%</span>
                            </div>
                            <Progress value={goal.progress} className="h-2" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Forecasts Tab */}
        <TabsContent value="forecasts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-cyan-500" />
                Goal Achievement Forecasts
              </CardTitle>
              <CardDescription>
                AI-powered predictions for when each goal will be achieved based on current trends
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!forecasts ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-12 w-12 text-muted-foreground mx-auto mb-3 animate-spin" />
                  <p className="text-muted-foreground">Loading forecasts...</p>
                </div>
              ) : forecasts.forecasts?.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-lg font-medium">All goals achieved!</p>
                  <p className="text-muted-foreground">No forecasts needed - you're at world-class performance.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">Expected Within 4 Weeks</p>
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                        {forecasts.forecasts?.filter(f => f.weeks_to_achieve && f.weeks_to_achieve <= 4).length || 0}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                      <p className="text-sm text-blue-600 dark:text-blue-400">Expected Within 12 Weeks</p>
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                        {forecasts.forecasts?.filter(f => f.weeks_to_achieve && f.weeks_to_achieve <= 12).length || 0}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-600 dark:text-amber-400">Needs Intervention</p>
                      <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                        {forecasts.forecasts?.filter(f => !f.weeks_to_achieve || f.weeks_to_achieve > 12).length || 0}
                      </p>
                    </div>
                  </div>

                  {/* Forecast List */}
                  <div className="space-y-3">
                    {forecasts.forecasts?.map((forecast, idx) => {
                      const config = categoryConfig[forecast.category] || categoryConfig.Quality;
                      const CategoryIcon = config.icon;
                      
                      return (
                        <div 
                          key={forecast.goal_id || idx}
                          className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1">
                              <div className={`p-2 rounded-lg ${config.bg}`}>
                                <CategoryIcon className={`h-5 w-5 ${config.color}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-semibold">{forecast.goal_name}</h4>
                                  <Badge variant="outline" className={config.bg}>
                                    {forecast.category}
                                  </Badge>
                                  {forecast.confidence && (
                                    <Badge 
                                      variant="secondary"
                                      className={
                                        forecast.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                                        forecast.confidence === 'medium' ? 'bg-blue-100 text-blue-700' :
                                        'bg-amber-100 text-amber-700'
                                      }
                                    >
                                      {forecast.confidence} confidence
                                    </Badge>
                                  )}
                                </div>
                                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground text-xs">Current</p>
                                    <p className="font-medium">{forecast.current_value}{forecast.unit}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-xs">Target</p>
                                    <p className="font-medium">{forecast.target}{forecast.unit}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-xs">Time to Achieve</p>
                                    <p className="font-medium">
                                      {forecast.weeks_to_achieve ? `${forecast.weeks_to_achieve} weeks` : 'Needs intervention'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-xs">Est. Achievement Date</p>
                                    <p className="font-medium">
                                      {forecast.estimated_achievement_date || 'TBD'}
                                    </p>
                                  </div>
                                </div>
                                {forecast.recommendation && (
                                  <div className="mt-3 p-2 rounded bg-muted/50">
                                    <p className="text-sm text-muted-foreground">
                                      <Sparkles className="h-3 w-3 inline mr-1" />
                                      {forecast.recommendation}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              {forecast.weeks_to_achieve ? (
                                forecast.weeks_to_achieve <= 4 ? (
                                  <div className="flex items-center gap-1 text-emerald-600">
                                    <CheckCircle2 className="h-5 w-5" />
                                    <span className="text-sm font-medium">Soon</span>
                                  </div>
                                ) : forecast.weeks_to_achieve <= 12 ? (
                                  <div className="flex items-center gap-1 text-blue-600">
                                    <Clock className="h-5 w-5" />
                                    <span className="text-sm font-medium">On Track</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 text-amber-600">
                                    <AlertTriangle className="h-5 w-5" />
                                    <span className="text-sm font-medium">Long Term</span>
                                  </div>
                                )
                              ) : (
                                <div className="flex items-center gap-1 text-red-600">
                                  <XCircle className="h-5 w-5" />
                                  <span className="text-sm font-medium">At Risk</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Priority Actions Tab */}
        <TabsContent value="actions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                Priority Action Items
              </CardTitle>
              <CardDescription>
                Actions prioritized by impact on overall excellence score
              </CardDescription>
            </CardHeader>
            <CardContent>
              {actions.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-lg font-medium">All goals are on track!</p>
                  <p className="text-muted-foreground">No priority actions needed at this time.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {actions.map((action, idx) => (
                    <div 
                      key={action.goal_id}
                      className={`p-4 rounded-lg border-l-4 ${
                        action.priority === 'CRITICAL' ? 'border-l-red-500 bg-red-50' :
                        action.priority === 'HIGH' ? 'border-l-amber-500 bg-amber-50' :
                        'border-l-blue-500 bg-blue-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl font-bold text-muted-foreground">#{idx + 1}</span>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">{action.goal_name}</h4>
                              <Badge 
                                variant={action.priority === 'CRITICAL' ? 'destructive' : action.priority === 'HIGH' ? 'default' : 'secondary'}
                              >
                                {action.priority}
                              </Badge>
                              <Badge variant="outline">{action.category}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{action.recommendation}</p>
                          </div>
                        </div>
                        
                        <div className="text-right shrink-0">
                          <p className="text-sm">
                            <span className="text-muted-foreground">Current:</span>{' '}
                            <span className="font-medium">{action.current_value ?? 'N/A'}</span>
                          </p>
                          <p className="text-sm">
                            <span className="text-muted-foreground">Target:</span>{' '}
                            <span className="font-medium text-emerald-600">{action.target}</span>
                          </p>
                          <p className="text-sm">
                            <span className="text-muted-foreground">Gap:</span>{' '}
                            <span className="font-medium text-amber-600">{action.gap}</span>
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center gap-4">
                        <Progress value={action.progress} className="flex-1 h-2" />
                        <span className="text-sm font-medium">{action.progress}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Benchmarks Tab */}
        <TabsContent value="benchmarks">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-violet-500" />
                Industry Benchmarks
              </CardTitle>
              <CardDescription>
                Targets based on CMS/CHAP 2025 standards and MedPAC recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {benchmarks?.sources && (
                <div className="mb-6 flex flex-wrap gap-2">
                  {benchmarks.sources.map((source, idx) => (
                    <TooltipProvider key={idx}>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge variant="outline" className="cursor-help">
                            {source.name}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{source.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Metric</th>
                      <th className="text-left py-2 px-3">Category</th>
                      <th className="text-center py-2 px-3">Target</th>
                      <th className="text-left py-2 px-3">Source</th>
                      <th className="text-left py-2 px-3">Percentile</th>
                      <th className="text-center py-2 px-3">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {benchmarks?.benchmarks?.map((bm, idx) => (
                      <tr key={bm.id} className={idx % 2 === 0 ? 'bg-muted/30' : ''}>
                        <td className="py-2 px-3 font-medium">{bm.name}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="text-xs">{bm.category}</Badge>
                        </td>
                        <td className="py-2 px-3 text-center font-mono">
                          {bm.direction === 'lower_is_better' && '≤'}
                          {bm.direction === 'higher_is_better' && '≥'}
                          {bm.target}{bm.unit}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{bm.benchmark_source}</td>
                        <td className="py-2 px-3 text-muted-foreground">{bm.benchmark_percentile}</td>
                        <td className="py-2 px-3 text-center">
                          <Badge 
                            variant={bm.priority === 'critical' ? 'destructive' : bm.priority === 'high' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {bm.priority}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
